import {CSaveApi, HOOKS} from '@wix/document-manager-extensions'
import {classic, Deferred, ReportableError} from '@wix/document-manager-utils'
import type {BIEvt, BIParams, LastSaveInfo, PS, Tags} from '@wix/document-services-types'
import * as santaCoreUtils from '@wix/santa-core-utils'
import experiment from 'experiment-amd'
import _ from 'lodash'
import bi from '../bi/bi'
import biErrors from '../bi/errors'
import constants from '../constants/constants'
import hooks from '../hooks/hooks'
import generalInfo from '../siteMetadata/generalInfo'
import type {SaveTaskRegistry, SaveTaskRegistryResult} from './lib/registry'
import saveTaskRunner from './lib/saveRunner'
import saveState from './lib/saveState'
import monitoring from './monitoring'
import preSaveOperations from './preSaveOperations/preSaveOperations'
import saveErrors, {
    convertToDSError,
    DSError,
    DSErrorWithReportableErrorDetails,
    getErrorTypeAndMessage
} from './saveErrors'
import {TaskQueue} from './TaskQueue'
import type {PublishAllOptions} from './saveTasks/saveDocument'
import {eventHooks} from '../hooks/eventHooks/eventHooks'

export interface SaveOptions extends PublishAllOptions {
    isPublish?: boolean
    isSilent?: boolean
    onBoarding?: boolean
    trigger?: string
    editorOrigin?: string
    fullPayload?: boolean
    viewerName?: string
    settleInServer?: boolean
    extraPayload?: any
    initiatorOrigin?: string
}

interface SaveTags {
    usingCSave: boolean
    usingCEdit: boolean
    isSaveFromDraft: boolean
}

/**
 * callback executed upon success
 */
export type OnSuccess = () => void

/**
 * callback executed upon error
 */
type OnError = (p: DSError) => void

export interface BICallbacks {
    event(reportDef: BIEvt, params?: BIParams): void
    error(reportDef: BIEvt, params?: BIParams): void
}

const reportError = (onError: OnError, error: any) => {
    const cb = onError ? onError : console.log
    cb(error)
}

const saveDisabled = (onError: OnError) => {
    reportError(onError, saveErrors.SAVE_DISABLED_IN_DOCUMENT_SERVICES)
}

function migrateFromOnBoardingIfNeeded(ps: PS, flag: boolean) {
    if (ps.config.origin === classic) {
        generalInfo.setUseOnBoarding(ps, flag)
    }
}

function setPublishSaveFlag(ps: PS) {
    const publishSaveInnerPointer = ps.pointers.general.getPublishSaveInnerPointer()
    ps.dal.set(publishSaveInnerPointer, true)
}

function setSilentSaveFlag(ps: PS) {
    const silentSaveInnerPointer = ps.pointers.general.getSilentSaveInnerPointer()
    ps.dal.set(silentSaveInnerPointer, true)
}

function getBiCallbacks(ps: PS): BICallbacks {
    return {
        event: _.partial(bi.event, ps),
        error: _.partial(bi.error, ps)
    }
}

function hasPermission(ps: PS, permission: string) {
    const permissions = generalInfo.getUserPermissions(ps)
    return _.includes(permissions, permission)
}

/**
 * Returns true if the user is allowed to publish according to server, false otherwise
 */
function canUserPublish(ps: PS): boolean {
    if (!saveState.isEnabled(ps)) {
        return false
    }
    const isOwnerAndPermitted = generalInfo.isOwner(ps) && !experiment.isOpen('dm_publishByPermissionsNotOwner')

    if (isOwnerAndPermitted) {
        return true
    }

    return hasPermission(ps, constants.PERMISSIONS.PUBLISH)
}

const hasSavePermissions = (ps: PS) => hasPermission(ps, constants.PERMISSIONS.SAVE)

function removeSaveFlags(ps: PS) {
    const publishSaveInnerPointer = ps.pointers.general.getPublishSaveInnerPointer()
    const silentSaveInnerPointer = ps.pointers.general.getSilentSaveInnerPointer()
    if (ps.dal.get(publishSaveInnerPointer)) {
        ps.dal.remove(publishSaveInnerPointer)
    } else if (ps.dal.get(silentSaveInnerPointer)) {
        ps.dal.remove(silentSaveInnerPointer)
    }
}

const runPresaveOperations = async (ps: PS, isFullSave: boolean, options: SaveOptions, tags: SaveTags) => {
    const {PRESAVE_OPERATIONS} = constants.INTERACTIONS.SAVE
    try {
        ps.extensionAPI.logger.interactionStarted(PRESAVE_OPERATIONS)
        ps.extensionAPI.logger.breadcrumb(`preSaveOperation on save started.`)
        await preSaveOperations.save(ps)
        ps.extensionAPI.logger.interactionEnded(PRESAVE_OPERATIONS)
    } catch (e: any) {
        const operationName = e.preSaveOperationName || 'unknownPreSaveOperationName'
        ps.extensionAPI.logger.breadcrumb(`preSaveOperation on save failed: ${operationName}`)
        ps.extensionAPI.logger.captureError(e, {
            tags: {preSaveOperationError: true, preSaveOperationName: operationName, isFullSave, ...tags}
        })
        const restoreMessage =
            e instanceof ReportableError && e.tags?.shouldRestoreRevision
                ? ' - must restore to the last successful revision'
                : ''
        santaCoreUtils.log.error(
            `Save has failed due to a preSaveOperation (${operationName}), and no request has been sent to the server${restoreMessage} - please see the failure details below:`,
            e
        )
        ps.dal.dropUncommittedTransaction(e.message)
        bi.error(ps, biErrors.SAVE_FAILED_DUE_TO_PRE_SAVE_OPERATION, e.stack)
        const dsError = saveErrors.normalizeError({
            documentServices: {
                errorCode: biErrors.SAVE_FAILED_DUE_TO_PRE_SAVE_OPERATION.errorCode,
                errorType: 'preSaveOperationSaveError',
                errorDescription: e
            },
            preSaveOperation: e
        })
        const dSErrorWithReportableErrorDetails: DSErrorWithReportableErrorDetails = {
            dsError,
            reportableErrorDetails: {
                tags: {
                    preSaveOperationError: true,
                    preSaveOperationName: operationName
                }
            }
        }
        throw dSErrorWithReportableErrorDetails
    }
}

const checkForPermissions = (ps: PS) => {
    const hasSave = hasSavePermissions(ps)
    if (!hasSave) {
        throw saveErrors.USER_NOT_AUTHORIZED_FOR_SITE
    }
}

const getSaveOptions = (ps: PS, options?: SaveOptions) => ({
    settleInServer: ps.runtimeConfig.settleInServer,
    shouldAvoidRevokingByConfig: ps.config.excludeRevoking,
    viewerName: ps.runtimeConfig.viewerName,
    editorOrigin: ps.runtimeConfig.editorType ?? ps.runtimeConfig.origin,
    initiatorOrigin: _.get(options, 'origin', '')
})

const waitForPendingCsaves = async (ps: PS, tags: SaveTags) => {
    try {
        await (ps.extensionAPI as CSaveApi).continuousSave.waitForCurrentPendingCsaves()
    } catch (e: any) {
        const err = saveErrors.createWaitForPendingCsavesError(ps, e)
        const {message, errorType} = getErrorTypeAndMessage(err)
        monitoring.error(new ReportableError({message, errorType, tags}), {errorDescription: err as any})
        throw saveErrors.normalizeError(err)
    }
}

const getEditorOriginSaveOption = (ps: PS) => ({editorOrigin: ps.runtimeConfig.origin})

export default (saveTaskRegistry: SaveTaskRegistry) => {
    const runAsyncPreSaveOperations = async (ps: PS, isFullSave: boolean, options: SaveOptions, tags: SaveTags) => {
        const isTemplate = ps.extensionAPI.siteAPI.isTemplate()
        if (!isTemplate) {
            checkForPermissions(ps)
        }
        await waitForPendingCsaves(ps, tags)
        migrateFromOnBoardingIfNeeded(ps, false)
        await runPresaveOperations(ps, isFullSave, options, tags)
    }

    const asyncDocumentMutationBeforeSave = async (
        ps: PS,
        isFullSave: boolean,
        options: SaveOptions,
        tags: SaveTags,
        _resolve: Function,
        reject: Function
    ) => {
        try {
            await runAsyncPreSaveOperations(ps, isFullSave, options, tags)
            ps.setOperationsQueue.asyncPreDataManipulationComplete(undefined)
        } catch (error: any) {
            ps.setOperationsQueue.asyncPreDataManipulationComplete(undefined, error.dsError || error)
            reject(error)
        }
    }

    const runSaveOperationAfterDocumentMutationDone = async (
        ps: PS,
        isFullSave: boolean,
        options: SaveOptions,
        tags: SaveTags
    ) => {
        const {isSaveFromDraft} = tags
        ps.extensionAPI.logger.breadcrumb(`preSaveOperation on save finished.`)
        const biCallbacks = getBiCallbacks(ps)

        if (options) {
            if (options.isPublish) {
                setPublishSaveFlag(ps)
            } else if (options.isSilent) {
                setSilentSaveFlag(ps)
            }
            if (!_.isUndefined(options.onBoarding)) {
                migrateFromOnBoardingIfNeeded(ps, options.onBoarding)
            }
        }
        saveState.setSaveProgress(ps, true)

        const handleSaveValidationError = () => {
            hooks.executeHook(hooks.HOOKS.SAVE.VALIDATION_ERROR, null!, [ps])
        }

        const saveErrorHandler = (e: string | DSError) => {
            const err = convertToDSError(e)
            const {message, errorType} = getErrorTypeAndMessage(err)
            monitoring.error(new ReportableError({message, errorType, tags}), {errorDescription: err as any})
            saveState.setSaveProgress(ps, false)
            removeSaveFlags(ps)
            if (saveErrors.isSaveValidationError(err)) {
                handleSaveValidationError()
            }
            throw saveErrors.normalizeError(err)
        }

        if (isFullSave) {
            await runFullSaveTasks(ps, options, biCallbacks).catch(saveErrorHandler)
        } else {
            await runPartialSaveTasks(ps, options, biCallbacks).catch(saveErrorHandler)
        }
        monitoring.end(monitoring.SAVE, options as Tags)
        removeSaveFlags(ps)
        saveState.setSaveProgress(ps, false)
        ps.extensionAPI.hooks.executeHook(HOOKS.SAVE_HOOKS.SITE_SAVED.createEvent({isSaveFromDraft}))
    }

    const runSaveOperationAfterDocumentMutationDoneAsyncWrapper = async (
        ps: PS,
        isFullSave: boolean,
        options: SaveOptions,
        tags: SaveTags,
        resolve: Function,
        reject: Function
    ) => {
        try {
            await runSaveOperationAfterDocumentMutationDone(ps, isFullSave, options, tags)
            resolve()
        } catch (e: any) {
            const {errorType, message} = getErrorTypeAndMessage(e)
            ps.extensionAPI.logger.captureError(
                new ReportableError({
                    errorType,
                    message,
                    tags: {saveFailure: 'asyncSaveWrapper'},
                    extras: {errorDescription: e}
                })
            )
            reject(e)
        }
    }

    const asyncSave = async (ps: PS, isFullSave: boolean, options: SaveOptions) => {
        monitoring.start(monitoring.SAVE, options as Tags)
        const isSaveFromDraft = generalInfo.isDraft(ps)
        const usingCSave = (ps.extensionAPI as CSaveApi).continuousSave.isCSaveOpen()
        const usingCEdit = (ps.extensionAPI as CSaveApi).continuousSave.isCEditOpen()
        const tags = {usingCSave, usingCEdit, isSaveFromDraft}

        if (ps.config.runPreSaveOperationInQueue) {
            const {promise, resolve, reject} = new Deferred()
            ps.setOperationsQueue.runSetOperation(
                runSaveOperationAfterDocumentMutationDoneAsyncWrapper,
                [ps, isFullSave, options, tags, resolve, reject],
                {
                    noBatching: false,
                    runWithPriority: true,
                    isAsyncOperation: true,
                    asyncPreDataManipulation: asyncDocumentMutationBeforeSave
                }
            )
            return promise
        }

        await runAsyncPreSaveOperations(ps, isFullSave, options, tags)
        await runSaveOperationAfterDocumentMutationDone(ps, isFullSave, options, tags)
    }

    const asyncSaveWrapper = async (
        ps: PS,
        isFullSave: boolean,
        options: SaveOptions,
        onSuccess: OnSuccess,
        onError: OnError
    ) => {
        try {
            await asyncSave(ps, isFullSave, options)
            onSuccess()
        } catch (e: any) {
            const {errorType, message} = getErrorTypeAndMessage(e.dsError || e)
            ps.extensionAPI.logger.captureError(
                new ReportableError({
                    errorType,
                    message,
                    tags: {...(e.reportableErrorDetails?.tags || {}), saveFailure: 'asyncSaveWrapper'},
                    extras: {errorDescription: e}
                })
            )
            onError(e.dsError || e)
        }
    }

    const save = async (
        ps: PS,
        onSuccess: OnSuccess,
        onError: OnError,
        isFullSave: boolean,
        options: SaveOptions
    ): Promise<void> => {
        await asyncSaveWrapper(ps, isFullSave, options, onSuccess, onError)
    }

    const runPresaveTasks = async (ps: PS, options: SaveOptions): Promise<void> => {
        if (options?.trigger !== 'CSAVE_NON_RECOVERABLE_ERROR') {
            try {
                ps.extensionAPI.logger.interactionStarted('presave_forceSaveAndWaitForResult')
                await ps.extensionAPI.continuousSave.forceSaveAndWaitForResult()
                ps.extensionAPI.logger.interactionEnded('presave_forceSaveAndWaitForResult')
            } catch (e) {
                throw saveErrors.createPresaveError(ps, e)
            }
        }
    }

    const runPartialSaveTasks = async (ps: PS, options: SaveOptions, biCallbacks: BICallbacks): Promise<void> => {
        try {
            const preSave = async () => {
                await runPresaveTasks(ps, options)
                ps.extensionAPI.continuousSave.disableSaveDuringRequiredAndPrimary(true)
            }
            await saveTaskRunner.promises.runPartialSaveTasks(
                saveTaskRegistry.getSaveTasksConfig(ps),
                ps,
                biCallbacks,
                getSaveOptions(ps, options),
                preSave
            )
        } finally {
            ps.extensionAPI.continuousSave.disableSaveDuringRequiredAndPrimary(false)
        }
    }

    const runFullSaveTasks = async (ps: PS, options: SaveOptions, biCallbacks: BICallbacks) => {
        await saveTaskRunner.promises.runFullSaveTasks(
            saveTaskRegistry.getSaveTasksConfig(ps),
            ps,
            biCallbacks,
            getSaveOptions(ps, options)
        )
    }

    const autosave = (ps: PS, onSuccess: OnSuccess = _.noop) => {
        //this method is deprecated
        onSuccess()
    }

    const saveAsTemplateAsync = async (ps: PS) => {
        try {
            await preSaveOperations.saveAsTemplate(ps)
        } catch (e: any) {
            ps.extensionAPI.logger.captureError(
                new ReportableError({
                    errorType: 'preSaveAsTemplateOperationSaveError',
                    message: e,
                    tags: {preSaveAsTemplateOperation: 'true'}
                })
            )

            throw saveErrors.normalizeError({preSaveAsTemplateOperation: e})
        }
        await saveAPI.promises.save(ps, false)
        await saveAPI.promises.publish(ps)
        await saveTaskRunner.promises.runSaveAsTemplate(
            saveTaskRegistry.getSaveTasksConfig(ps),
            ps,
            getBiCallbacks(ps),
            getEditorOriginSaveOption(ps)
        )
        await ps.extensionAPI.rendererModel.refreshClientSpecMap()
    }

    const saveAsTemplate = (ps: PS, onSuccess: OnSuccess, onError: OnError) => {
        // eslint-disable-next-line promise/prefer-await-to-then
        saveAsTemplateAsync(ps).then(onSuccess, onError)
    }

    const shouldSaveBeforePublish = (saveConfig: SaveTaskRegistryResult, ps: PS) =>
        saveTaskRunner.shouldSaveBeforePublish(saveConfig, ps)

    const publishAsync = async (ps: PS, options: SaveOptions) => {
        if (ps.config.allowOnlyPublishRC && !options?.publishRC) {
            throw new ReportableError({
                message: 'Only publish RC is allowed',
                errorType: 'ONLY_PUBLISH_RC_ALLOWED',
                extras: {options}
            })
        }

        const saveTasksConfig = saveTaskRegistry.getSaveTasksConfig(ps)
        const innerOptions = getSaveOptions(ps)
        const extendedOptions = _.assign({}, innerOptions, options, {
            cachePublishedSite: !!ps.config.cachePublishedSite
        })
        try {
            if (shouldSaveBeforePublish(saveTasksConfig, ps)) {
                ps.extensionAPI.logger.breadcrumb('shouldSaveBeforePublish - saving before publishing')
                ps.extensionAPI.logger.interactionStarted(constants.INTERACTIONS.SAVE_BEFORE_PUBLISH)
                await saveAPI.promises.saveWithoutQueue(ps, false, options)
                ps.extensionAPI.logger.interactionEnded(constants.INTERACTIONS.SAVE_BEFORE_PUBLISH)
            } else {
                ps.extensionAPI.logger.breadcrumb('shouldSaveBeforePublish - no need to save, only publishing')
            }
            saveState.setPublishProgress(ps, true)
            hooks.executeHook(hooks.HOOKS.PUBLISH.BEFORE, null!, [ps])
            await saveTaskRunner.promises.runPublishTasks(
                saveTasksConfig,
                ps,
                getBiCallbacks(ps),
                extendedOptions,
                async () => {
                    ps.extensionAPI.continuousSave.disableSaveDuringRequiredAndPrimary(true)
                }
            )
        } finally {
            ps.extensionAPI.continuousSave.disableSaveDuringRequiredAndPrimary(false)
            saveState.setPublishProgress(ps, false)
        }
    }

    const publishAsyncWrapper = async (ps: PS, options: SaveOptions) => {
        try {
            await publishAsync(ps, options)
        } catch (e) {
            const {errorType, message} = getErrorTypeAndMessage(e)
            ps.extensionAPI.logger.captureError(
                new ReportableError({
                    errorType,
                    message,
                    tags: {publishFailure: 'publishAsyncWrapper'},
                    extras: {errorDescription: e}
                })
            )
            throw e
        }
    }

    const publish = (ps: PS, onSuccess: OnSuccess, onError: OnError, options: SaveOptions) => {
        publishAsyncWrapper(ps, options).then(onSuccess, onError) // eslint-disable-line promise/prefer-await-to-then
    }

    const registerToDeploymentStatus = (ps: PS, callback) => {
        eventHooks(ps).registerHook(HOOKS.PUBLISH_HOOKS.PUBLISH.STATUS.id, callback)
    }

    const canSave = (ps: PS) => saveState.isSaveAllowed(ps)

    const asyncF = (f: Function, ps: PS, ...rest: any[]) =>
        new Promise<void>((resolve, reject) => {
            f(ps, resolve, reject, ...rest)
        })

    const queue: TaskQueue = new TaskQueue()

    const ifSaveAllowed =
        (f: Function, shouldQueue: boolean = false) =>
        (ps: PS, onSuccess: OnSuccess, onError: OnError, ...rest: any[]): void => {
            if (canSave(ps)) {
                if (shouldQueue) {
                    queue.run(async () => {
                        await asyncF(f, ps, ...rest).then(onSuccess, onError)
                    })
                    return
                }
                return f(ps, onSuccess, onError, ...rest)
            }
            if (!saveState.isEnabled(ps)) {
                return saveDisabled(onError)
            }
        }

    const initMethod = (
        ps: PS,
        {disableSave = false, settleInServer = true}: {disableSave?: boolean; settleInServer?: boolean} = {
            disableSave: false,
            settleInServer: true
        }
    ) => {
        saveState.setSaveAllowed(ps, !disableSave)
        ps.runtimeConfig.settleInServer = settleInServer
    }

    const saveIf = ifSaveAllowed(save, true)

    const getLastSaveInfo = async (ps: PS): Promise<LastSaveInfo> => {
        const date = await (ps.extensionAPI as CSaveApi).continuousSave.getLastSaveDate()
        return {date}
    }

    const resetRevisionWithoutTransactions = async (ps: PS, reason: string): Promise<void> => {
        ps.extensionAPI.logger.interactionStarted('resetRevisionWithoutTransactions', {extras: {reason}})
        try {
            const csaveAPI = ps.extensionAPI as CSaveApi
            await csaveAPI.continuousSave.createRevisionWithSuppressTransactions()
        } catch (error) {
            const reportableError = new ReportableError({
                message: 'Failed to drop unapplied transactions',
                errorType: 'DROP_UNAPPLIED_TRANSACTIONS_ERROR',
                extras: {reason, error: String(error)}
            })
            ps.extensionAPI.logger.captureError(reportableError)
            throw reportableError
        }
        ps.extensionAPI.logger.interactionEnded('resetRevisionWithoutTransactions', {extras: {reason}})
    }

    const restoreRevision = async (
        ps: PS,
        options?: {reason?: string; revisionId?: string; branchId?: string}
    ): Promise<void> => {
        const {RESTORE_REVISION} = constants.INTERACTIONS.SAVE
        ps.extensionAPI.logger.interactionStarted(RESTORE_REVISION, {
            extras: {reason: options?.reason ?? ''}
        })
        const siteId = generalInfo.getSiteId(ps)

        const targetBranchId = options?.branchId ?? generalInfo.getBranchId(ps)
        let targetRevisionId: string
        if (options?.revisionId) {
            targetRevisionId = options.revisionId
        } else if (options?.branchId) {
            try {
                targetRevisionId = await ps.extensionAPI.branches.getBranchRevision(options.branchId)
            } catch (error) {
                const reportableError = new ReportableError({
                    message: `Failed to get latest revision for branch ${options.branchId}`,
                    errorType: 'FETCH_BRANCH_REVISION_ERROR',
                    extras: {branchId: options.branchId, error: String(error)}
                })
                ps.extensionAPI.logger.captureError(reportableError)
                throw reportableError
            }
        } else {
            targetRevisionId = generalInfo.getRevision(ps).toString()
        }

        try {
            const csaveAPI = ps.extensionAPI as CSaveApi
            await csaveAPI.continuousSave.restoreRevision(targetRevisionId, siteId, targetBranchId)
        } catch (error: any) {
            const requestId = error?.requestId || 'unknown'
            throw new Error(`cannot restore revision, request id: ${requestId}`)
        } finally {
            ps.extensionAPI.logger.interactionEnded(RESTORE_REVISION)
        }
    }

    const saveAPI = {
        initMethod,
        /**
         * performs a partial save
         * @param onSuccess
         * @param onError
         * @param isFullSave whether to perform a full save or not
         * @param options
         * @param privateServices
         */
        save: saveIf,

        saveWithoutQueue: ifSaveAllowed(save),
        /**
         * @deprecated
         * performs an autosave.
         * You should call autosave from the public autosave endpoint, which validates that you can actually autosave
         * @param privateServices
         * @param onSuccess
         * @param onError
         */
        autosave: ifSaveAllowed(autosave),

        saveAsTemplate: ifSaveAllowed(saveAsTemplate),

        /**
         * publishes the site
         * @param privateServices
         * @param onSuccess
         * @param onError
         * @param options
         */
        publish: ifSaveAllowed(publish, true),

        registerToDeploymentStatus,

        canUserPublish,

        saveState,

        getLastSaveInfo,

        restoreRevision,

        resetRevisionWithoutTransactions,

        promises: {
            /**
             * performs a partial save
             * @param ps
             * @param [isFullSave] whether to perform a full save or not
             * @param [options]
             */
            save: (ps: PS, isFullSave: boolean = false, options?: SaveOptions) =>
                new Promise<void>((resolve, reject) => {
                    saveAPI.save(ps, resolve, reject, isFullSave, options)
                }),
            /**
             * performs a partial save
             * @param ps
             * @param [isFullSave] whether to perform a full save or not
             * @param [options]
             */
            saveWithoutQueue: (ps: PS, isFullSave: boolean = false, options?: SaveOptions) =>
                new Promise<void>((resolve, reject) => {
                    saveAPI.saveWithoutQueue(ps, resolve, reject, isFullSave, options)
                }),

            /**
             * performs an autosave.
             * You should call autosave from the public autosave endpoint, which validates that you can actually autosave
             */
            autosave: (ps: PS) =>
                new Promise<void>((resolve, reject) => {
                    saveAPI.autosave(ps, resolve, reject)
                }),

            /**
             * publishes the site
             */
            publish: (ps: PS, options?: SaveOptions) =>
                new Promise<void>((resolve, reject) => {
                    saveAPI.publish(ps, resolve, reject, options)
                }),

            saveAsTemplate: (ps: PS) =>
                new Promise<void>((resolve, reject) => {
                    saveAPI.saveAsTemplate(ps, resolve, reject)
                }),

            restoreRevision: (ps: PS, options?: {reason?: string; revisionId?: string; branchId?: string}) =>
                saveAPI.restoreRevision(ps, options),

            resetRevisionWithoutTransactions: (ps: PS, reason: string) =>
                saveAPI.resetRevisionWithoutTransactions(ps, reason)
        }
    }

    return saveAPI
}
